import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { noClientsDemoI18n } from '../i18n';
import { isVentaCall } from '../callKind';
import {
  getNoClientsDemoCallsByDeal,
  getNoClientsConsolidatedAnalysis,
  getNoClientsEmailThread,
  getNoClientsWhatsappThread,
} from '../../../services/noClientsDemoService';
import usePermissionsAI from 'permissions/modules/artificialIntelligence/usePermissionsAI';
import { toUtcMs, formatChileDate, formatChileDateTime } from './threadUtils';
import Accordions from '../../../components/Accordion/Accordions';
import './clientDetail.scss';

// Parsea el analysisJson de una llamada (string, string doble-encode u objeto; admite envoltura .data).
const parseAnalysisJson = (raw) => {
  if (!raw) return null;
  try {
    let obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (typeof obj === 'string') obj = JSON.parse(obj);
    if (obj?.data && (obj.data.datos_llamada || obj.data.evaluacion_protocolo))
      return obj.data;
    return obj;
  } catch (e) {
    return null;
  }
};

// Parseo seguro del analysisJson consolidado (string, doble-encode u objeto).
const safeParseJson = (raw) => {
  if (!raw) return null;
  try {
    let obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (typeof obj === 'string') obj = JSON.parse(obj);
    return obj && typeof obj === 'object' ? obj : null;
  } catch (e) {
    return null;
  }
};

// Color del badge segun la evaluacion general.
const evalColorOf = (value) => {
  const v = (value || '').toString().toLowerCase();
  if (v === 'buena' || v === 'si' || v === 'sí') return 'success';
  if (v === 'regular' || v === 'parcial') return 'warning';
  if (v === 'deficiente' || v === 'no') return 'danger';
  return 'primary';
};

// % de cumplimiento del protocolo = promedio de porcentaje_cumplimiento de los pasos.
const overallPercentOf = (analysis) => {
  const evaluation = Array.isArray(analysis?.evaluacion_protocolo)
    ? analysis.evaluacion_protocolo
    : [];
  const nums = evaluation
    .map((x) => Number(x?.porcentaje_cumplimiento))
    .filter((n) => !Number.isNaN(n));
  if (!nums.length) return null;
  return Math.round(nums.reduce((a, b) => a + b, 0) / nums.length);
};

// Fechas del backend en UTC -> mostramos en hora de Chile (America/Santiago), igual que HubSpot.
// Fuente única en dateUtils (re-exportada por threadUtils).
const formatDateTime = (value, fallback) => formatChileDateTime(value, fallback);
const formatDate = (value) => formatChileDate(value, '--');

const ClientDetail = ({ DesignSystemComponents }) => {
  const { Card, Table, Button, IconButton, Badge, Dropdown, SvgIcon } =
    DesignSystemComponents || {};
  const navigate = useNavigate();
  const { dealId } = useParams();
  const { t } = useScopedTranslation(noClientsDemoI18n);
  const { canViewNoClientesCall } = usePermissionsAI();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [contactPage, setContactPage] = useState(0);
  const [contactPageSize, setContactPageSize] = useState(10);

  // Análisis comercial consolidado (todas las llamadas del cliente).
  const [consolidated, setConsolidated] = useState(null);
  const [consolidatedLoading, setConsolidatedLoading] = useState(true);
  const [consolidatedError, setConsolidatedError] = useState(null);

  // Hilos de contacto (email / whatsapp) para el "último contacto".
  const [emailThread, setEmailThread] = useState(null);
  const [whatsappThread, setWhatsappThread] = useState(null);
  const [threadsLoading, setThreadsLoading] = useState(true);

  const loadConsolidated = useCallback(
    async (dealIdArg, signal) => {
      try {
        setConsolidatedLoading(true);
        setConsolidatedError(null);
        const data = await getNoClientsConsolidatedAnalysis(dealIdArg, signal);
        setConsolidated(data); // null => aún no calculado
      } catch (e) {
        if (e.name !== 'AbortError' && e.name !== 'CanceledError') {
          console.error('Error cargando análisis consolidado:', e);
          setConsolidatedError(t('nc_cd_analysisError'));
        }
      } finally {
        setConsolidatedLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    if (!dealId) return undefined;
    const controller = new AbortController();
    loadConsolidated(dealId, controller.signal);
    return () => controller.abort();
  }, [dealId, loadConsolidated]);

  // Trae ambos hilos (email + whatsapp). El "último contacto" y qué hilo mostrar se decide
  // después comparando la última interacción de cada canal contra la última llamada.
  useEffect(() => {
    if (!dealId) return undefined;
    const controller = new AbortController();
    const loadThreads = async () => {
      try {
        setThreadsLoading(true);
        const [emails, wsp] = await Promise.all([
          getNoClientsEmailThread(dealId, controller.signal),
          getNoClientsWhatsappThread(dealId, controller.signal),
        ]);
        setEmailThread(Array.isArray(emails) ? emails : []);
        setWhatsappThread(Array.isArray(wsp) ? wsp : []);
      } catch (e) {
        if (e.name !== 'AbortError' && e.name !== 'CanceledError') {
          console.error('Error cargando hilos de contacto:', e);
          setEmailThread([]);
          setWhatsappThread([]);
        }
      } finally {
        setThreadsLoading(false);
      }
    };
    loadThreads();
    return () => controller.abort();
  }, [dealId]);

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      if (!dealId) {
        setError(t('nc_cd_error'));
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const data = await getNoClientsDemoCallsByDeal(
          dealId,
          controller.signal
        );
        setDetail(data);
      } catch (e) {
        if (e.name !== 'AbortError' && e.name !== 'CanceledError') {
          console.error('Error cargando detalle del cliente:', e);
          setError(t('nc_cd_error'));
          setDetail(null);
        }
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [dealId]);

  const handleViewCall = useCallback(
    (call) => {
      // Demo: los endpoints de transcripcion/audio se identifican por ConnId.
      const params = new URLSearchParams({
        id: String(call?.connId || ''),
        dealName: detail?.dealName || '',
        hubspotOwnerName: detail?.hubspotOwnerName || '',
      });
      navigate(`/ia/no-clientes/analisis-protocolo?${params}`);
    },
    [navigate, detail]
  );

  const handleViewTranscript = useCallback(
    (call) => {
      const connId = String(call?.connId || '');
      if (!connId) return;
      navigate(
        `/ia/no-clientes/analisis-protocolo/transcripcion/${encodeURIComponent(connId)}`
      );
    },
    [navigate]
  );

  // Numeramos las llamadas (vienen en orden cronológico desde el backend).
  const calls = useMemo(
    () =>
      (Array.isArray(detail?.calls) ? detail.calls : []).map((c, i) => ({
        ...c,
        _num: i + 1,
      })),
    [detail]
  );

  // Una fila por CORREO y una fila por CONVERSACIÓN de WhatsApp (agrupada por ThreadId),
  // todo ordenado por fecha (igual que la tabla de llamadas). El botón "Ver" abre esa
  // interacción puntual (un correo / una conversación) en la vista aparte.
  const contactRows = useMemo(() => {
    const rows = [];
    (emailThread || []).forEach((e) => {
      rows.push({
        canal: 'email',
        ref: e.hubspotEmailId,
        dateStr: e.occurredAt,
        dateMs: toUtcMs(e.occurredAt),
      });
    });
    // Agrupa los mensajes de WhatsApp por conversación (ThreadId) → una fila por conversación.
    const byThread = new Map();
    (whatsappThread || []).forEach((m) => {
      const tid = m.threadId || '_';
      if (!byThread.has(tid)) byThread.set(tid, []);
      byThread.get(tid).push(m);
    });
    byThread.forEach((msgs, tid) => {
      const lastMs = msgs.reduce(
        (mx, m) => Math.max(mx, toUtcMs(m.occurredAt)),
        0
      );
      const last = msgs.reduce(
        (acc, m) =>
          toUtcMs(m.occurredAt) >= toUtcMs(acc.occurredAt) ? m : acc,
        msgs[0]
      );
      rows.push({
        canal: 'whatsapp',
        ref: tid,
        dateStr: last?.occurredAt,
        dateMs: lastMs,
        count: msgs.length,
      });
    });
    // Orden cronológico (igual que llamadas) y numeramos.
    return rows
      .sort((a, b) => a.dateMs - b.dateMs)
      .map((r, i) => ({ ...r, _num: i + 1 }));
  }, [whatsappThread, emailThread]);

  const handleViewThread = useCallback(
    (canal, ref) => {
      if (!dealId || !canal) return;
      const seg = canal === 'whatsapp' ? 'whatsapp' : 'correo';
      navigate(
        `/ia/no-clientes/cliente/${encodeURIComponent(dealId)}/contacto/${seg}/${encodeURIComponent(ref ?? '')}`
      );
    },
    [navigate, dealId]
  );

  // Al cambiar de cliente, volver a la primera pagina.
  useEffect(() => {
    setPage(0);
    setContactPage(0);
  }, [dealId]);

  // Comparadores client-side por columna (sort estilo tabla no-clientes). El '#' queda como
  // el orden cronologico original de la llamada.
  const sortFns = useMemo(() => {
    const scoreOf = (row) =>
      overallPercentOf(parseAnalysisJson(row?.analyses?.[0]?.analysisJson));
    const str = (v) => (v || '').toString();
    return {
      callDateTime: (a, b) =>
        new Date(a?.callDateTime || 0) - new Date(b?.callDateTime || 0),
      callKind: (a, b) => str(a?.callKind).localeCompare(str(b?.callKind)),
      score: (a, b) => (scoreOf(a) ?? -1) - (scoreOf(b) ?? -1),
      feeling: (a, b) => str(a?.feeling).localeCompare(str(b?.feeling)),
    };
  }, []);

  const columns = useMemo(
    () => [
      { accessor: '_num', label: '#', width: '64px' },
      {
        accessor: 'callDateTime',
        label: t('nc_ccd_callDate'),
        sort: true,
        render: (row) => formatDateTime(row.callDateTime, t('nc_na')),
      },
      {
        accessor: 'callKind',
        label: t('nc_ccd_callKind'),
        sort: true,
        render: (row) => {
          if (!row.callKind) return '--';
          const isVenta = row.callKind === 'Venta';
          // Baja confianza (solo venta): telefono resuelto por cadena recuperada o con varias
          // preventas candidatas. Se marca discreto (badge atenuado + tooltip), no se oculta.
          const lowConf =
            isVenta && (row.confidence === 'medium' || row.isAmbiguous);
          const badge = (
            <Badge
              text={t(isVenta ? 'nc_tv_sale' : 'nc_tv_presale')}
              color={isVenta ? 'success' : 'primary'}
              size='sm'
            />
          );
          return lowConf ? (
            <span title={t('nc_ccd_kindLowConf')} style={{ opacity: 0.65 }}>
              {badge}
            </span>
          ) : (
            badge
          );
        },
      },
      {
        accessor: 'score',
        label: t('nc_ccd_score'),
        align: 'right',
        sort: true,
        // El % de protocolo de VENTA no aplica a preventa: se oculta.
        render: (row) => {
          if (!isVentaCall(row)) return '--';
          const p = overallPercentOf(
            parseAnalysisJson(row?.analyses?.[0]?.analysisJson)
          );
          return p === null ? '--' : `${p}%`;
        },
      },
      {
        accessor: 'feeling',
        label: t('nc_tv_feeling'),
        sort: true,
        // Sentimiento oculto en preventa.
        render: (row) => (isVentaCall(row) ? row.feeling || '--' : '--'),
      },
      {
        label: t('nc_col_actions'),
        align: 'right',
        // Menú kebab (3 puntitos): Ver transcripción (siempre) + Ver análisis (SOLO venta).
        render: (row) => {
          if (!canViewNoClientesCall) {
            return (
              <IconButton
                ariaLabel={t('nc_col_actions')}
                icon='threeDotsVertical'
                size='md'
                disabled
              />
            );
          }
          const options = [
            { label: t('nc_aria_viewTranscript'), value: 'transcript' },
          ];
          if (isVentaCall(row)) {
            options.push({
              label: t('nc_aria_viewAnalysis'),
              value: 'analysis',
            });
          }
          return (
            <Dropdown
              ariaLabel={t('nc_col_actions')}
              icon='threeDotsVertical'
              variant='icon'
              options={options}
              onSelect={(selected) => {
                if (selected?.value === 'analysis') handleViewCall(row);
                else if (selected?.value === 'transcript')
                  handleViewTranscript(row);
              }}
            />
          );
        },
      },
    ],
    [
      t,
      IconButton,
      Dropdown,
      Badge,
      canViewNoClientesCall,
      handleViewCall,
      handleViewTranscript,
    ]
  );

  // Tabla de contactos (email / WhatsApp): mismas columnas que llamadas (# / Fecha / Tipo / Ver).
  const contactColumns = useMemo(
    () => [
      { accessor: '_num', label: '#', width: '64px' },
      {
        accessor: 'dateMs',
        label: t('nc_ccd_callDate'),
        sort: true,
        render: (row) => formatDateTime(row.dateStr, t('nc_na')),
      },
      {
        accessor: 'canal',
        label: t('nc_ccd_callKind'),
        sort: true,
        // Ícono del canal (patrón de mensajeria/crear-mensajes): whatsapp/send + color por className.
        render: (row) => {
          const isWa = row.canal === 'whatsapp';
          const label = isWa ? t('nc_ch_whatsapp') : t('nc_ch_email');
          const icon = isWa ? 'whatsapp' : 'send';
          const color = isWa ? 'text-success' : 'text-primary';
          return (
            <div className='d-flex align-items-center gap-2'>
              {SvgIcon && <SvgIcon name={icon} size='16px' className={color} />}
              <span>{label}</span>
            </div>
          );
        },
      },
      {
        label: t('nc_col_actions'),
        align: 'right',
        width: '90px',
        render: (row) => (
          <IconButton
            ariaLabel={t('nc_ct_view')}
            icon='eye'
            size='md'
            iconStroke='var(--primary-500)'
            onClick={() => handleViewThread(row.canal, row.ref)}
            disabled={!canViewNoClientesCall}
          />
        ),
      },
    ],
    [t, SvgIcon, IconButton, canViewNoClientesCall, handleViewThread]
  );

  // Conteo por canal para mostrarlo en el título del acordeón (saber si hay algo antes de abrir).
  const contactCounts = useMemo(
    () => ({
      email: contactRows.filter((r) => r.canal === 'email').length,
      whatsapp: contactRows.filter((r) => r.canal === 'whatsapp').length,
    }),
    [contactRows]
  );

  // Comparadores client-side de la tabla de contactos (por fecha / por tipo).
  const contactSortFns = useMemo(() => {
    const str = (v) => (v || '').toString();
    return {
      dateMs: (a, b) => (a.dateMs || 0) - (b.dateMs || 0),
      canal: (a, b) => str(a.canal).localeCompare(str(b.canal)),
    };
  }, []);

  if (!Card || !Table) return null;

  return (
    <div className='no-clients-page client-detail-page'>
      <div className='d-flex align-items-center gap-2 mb-2'>
        {Button ? (
          <Button
            variant='naked'
            iconStart='chevronLeft'
            label={t('nc_back')}
            size='sm'
            onClick={() => navigate(-1)}
          />
        ) : (
          <button type='button' onClick={() => navigate(-1)}>
            {t('nc_back')}
          </button>
        )}
      </div>

      <div className='mb-4'>
        <h2 className='fw-semibold'>{t('nc_cd_title')}</h2>
        <h4 className='fw-normal'>{t('nc_cd_subtitle')}</h4>
      </div>

      {error && <p className='text-danger'>{error}</p>}
      {loading && (
        <p className='caption color-gray-600'>{t('nc_ccd_loading')}</p>
      )}

      {!loading && detail && (
        <>
          {/* Cabecera del cliente */}
          <Card specialPadding='px-4 pt-4 pb-4' className='mb-4'>
            <div className='row'>
              <div className='col'>
                <strong>{t('nc_pa_client')}</strong> {detail.dealName || '--'}
              </div>
              <div className='col'>
                <strong>{t('nc_pa_consultant')}</strong>{' '}
                {detail.hubspotOwnerName || '--'}
              </div>
              <div className='col'>
                <strong>{t('nc_col_phone')}:</strong> {detail.phone || '--'}
              </div>
            </div>
            <div className='row mt-2'>
              <div className='col'>
                <strong>{t('nc_col_lostReason')}:</strong>{' '}
                {detail.lostReason || '--'}
              </div>
              <div className='col'>
                <strong>{t('nc_col_closeDate')}:</strong>{' '}
                {formatDate(detail.closeDate)}
              </div>
              <div className='col'>
                <strong>{t('nc_col_callCount')}:</strong>{' '}
                {detail.callCount ?? 0}
              </div>
            </div>
            <div className='row mt-2'>
              <div className='col'>
                <strong>{t('nc_cd_dealId')}:</strong> {detail.dealId || '--'}
              </div>
              <div className='col'>
                <strong>{t('nc_cd_createDate')}:</strong>{' '}
                {detail.createDate ? formatDate(detail.createDate) : '--'}
              </div>
              <div className='col'></div>
            </div>
          </Card>

          {/* Análisis consolidado: acordeón (cerrado por defecto) */}
          <Card specialPadding='px-4 pt-4 pb-4' className='mb-4'>
            <Accordions
              DesignSystemComponents={DesignSystemComponents}
              items={[
                {
                  title: (
                    <h3 className='mb-0'>
                      {t('nc_cd_analysisTitle')}
                      {!consolidatedLoading && !consolidated && (
                        <span className='caption color-gray-600'>
                          {' '}
                          ({t('nc_cd_analysisNoData')})
                        </span>
                      )}
                    </h3>
                  ),
                  content: (
                    <>
                      {consolidatedLoading && (
                        <p className='caption color-gray-600 mb-0'>
                          {t('nc_cd_analysisLoading')}
                        </p>
                      )}

                      {!consolidatedLoading && consolidatedError && (
                        <div className='client-detail-wip'>
                          <p className='text-danger mb-2'>
                            {consolidatedError}
                          </p>
                          {Button && (
                            <Button
                              variant='secondary'
                              label={t('nc_cd_analysisRetry')}
                              size='sm'
                              onClick={() => loadConsolidated(dealId)}
                            />
                          )}
                        </div>
                      )}

                      {!consolidatedLoading &&
                        !consolidatedError &&
                        !consolidated && (
                          <p className='mb-0 color-gray-600'>
                            {t('nc_cd_analysisEmpty')}
                          </p>
                        )}

                      {!consolidatedLoading &&
                        !consolidatedError &&
                        consolidated &&
                        (() => {
                          const a = safeParseJson(consolidated.analysisJson);
                          if (!a) {
                            return (
                              <p className='mb-0 color-gray-600'>
                                {t('nc_cd_analysisEmpty')}
                              </p>
                            );
                          }
                          const verdict =
                            a.evaluacion_general ?? a.cumple_protocolo;
                          const strengths = Array.isArray(a.fortalezas)
                            ? a.fortalezas
                            : [];
                          const gaps = Array.isArray(a.oportunidades_mejora)
                            ? a.oportunidades_mejora
                            : Array.isArray(a.incumplimientos)
                              ? a.incumplimientos
                              : [];
                          return (
                            <>
                              <div className='d-flex align-items-center gap-2 mb-3 flex-wrap'>
                                {verdict && (
                                  <Badge
                                    text={String(verdict).toUpperCase()}
                                    color={evalColorOf(verdict)}
                                    size='md'
                                  />
                                )}
                                {a.puntaje !== undefined &&
                                  a.puntaje !== null && (
                                    <span className='fw-semibold'>{`${a.puntaje}/100`}</span>
                                  )}
                              </div>

                              {a.resumen && <p className='mb-3'>{a.resumen}</p>}

                              {strengths.length > 0 && (
                                <div className='mb-3'>
                                  <strong>
                                    {t('nc_cd_analysisStrengths')}
                                  </strong>
                                  <ul className='mb-0 mt-1'>
                                    {strengths.map((s, i) => (
                                      <li key={i}>{s}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {gaps.length > 0 && (
                                <div className='mb-3'>
                                  <strong>{t('nc_cd_analysisGaps')}</strong>
                                  <ul className='mb-0 mt-1'>
                                    {gaps.map((s, i) => (
                                      <li key={i}>{s}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {a.observaciones &&
                                String(a.observaciones).trim() && (
                                  <div className='mb-0'>
                                    <strong>{t('nc_cd_analysisNotes')}</strong>
                                    <p className='mb-0 mt-1 color-gray-600'>
                                      {a.observaciones}
                                    </p>
                                  </div>
                                )}
                            </>
                          );
                        })()}
                    </>
                  ),
                },
              ]}
            />
          </Card>

          {/* Llamadas del cliente: acordeón (cerrado por defecto) */}
          <Card specialPadding='px-4 pt-4 pb-2' className='mb-4'>
            <Accordions
              DesignSystemComponents={DesignSystemComponents}
              items={[
                {
                  title: (
                    <h3 className='mb-0'>
                      {t('nc_cd_callsTitle')}{' '}
                      <span className='caption color-gray-600'>
                        ({calls.length} {t('nc_cd_callsLabel')})
                      </span>
                    </h3>
                  ),
                  content: (
                    <div className='table-container'>
                      <Table
                        columns={columns}
                        data={calls}
                        sortFns={sortFns}
                        paginator
                        page={page}
                        onPageChange={setPage}
                        pageSize={pageSize}
                        onPageSizeChange={(size) => {
                          setPageSize(size);
                          setPage(0);
                        }}
                        paginatorGroup={[
                          { label: '10', value: 10 },
                          { label: '20', value: 20 },
                          { label: '50', value: 50 },
                        ]}
                      />
                    </div>
                  ),
                },
              ]}
            />
          </Card>

          {/* Otros contactos: acordeón (cerrado por defecto) */}
          <Card specialPadding='px-4 pt-4 pb-2' className='mb-4'>
            <Accordions
              DesignSystemComponents={DesignSystemComponents}
              items={[
                {
                  title: (
                    <h3 className='mb-0'>
                      {t('nc_cd_contactsTitle')}{' '}
                      <span className='caption color-gray-600'>
                        {threadsLoading
                          ? '…'
                          : `(${contactCounts.email} ${t('nc_cd_emailsLabel')} · ${contactCounts.whatsapp} ${t('nc_ch_whatsapp')})`}
                      </span>
                    </h3>
                  ),
                  content: threadsLoading ? (
                    <p className='caption color-gray-600 mb-3'>
                      {t('nc_cd_threadLoading')}
                    </p>
                  ) : contactRows.length === 0 ? (
                    <p className='caption color-gray-600 mb-3'>
                      {t('nc_cd_contactsEmpty')}
                    </p>
                  ) : (
                    <div className='table-container'>
                      <Table
                        columns={contactColumns}
                        data={contactRows}
                        sortFns={contactSortFns}
                        paginator
                        page={contactPage}
                        onPageChange={setContactPage}
                        pageSize={contactPageSize}
                        onPageSizeChange={(size) => {
                          setContactPageSize(size);
                          setContactPage(0);
                        }}
                        paginatorGroup={[
                          { label: '10', value: 10 },
                          { label: '20', value: 20 },
                          { label: '50', value: 50 },
                        ]}
                      />
                    </div>
                  ),
                },
              ]}
            />
          </Card>
        </>
      )}
    </div>
  );
};

export default ClientDetail;
